// shipping/holiday logic, pinned to America/New_York.
// We can call these from client or backend; results are identical if we inject the same "now".

import {
  FACTORY_TZ,
  fmtInTZ,
  fmtFactoryDate,
  cutoffUTC,
  startOfFactoryDayUTC,
  factoryLocalToUTC,
  systemClock,
  addFactoryDaysUTC,
} from './time-utils.js';

/* ---------------- Display helpers ---------------- */

// format "Wednesday (MM/DD)" in FACTORY_TZ
const formatWeekDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return fmtInTZ(d, FACTORY_TZ, 'eeee (MM/dd)');
};

// YYYY-MM-DD in the factory calendar
const formatDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return fmtFactoryDate(d);
};

/* ---------------- Holiday helpers ---------------- */

// Helper function to calculate the nth weekday of a month
const getNthWeekdayOfMonth = (nth, weekday, month, year) => {
  const firstDayOfMonth = new Date(year, month, 1);
  const firstWeekday = firstDayOfMonth.getDay();
  const dayOffset = (weekday - firstWeekday + 7) % 7;
  return 1 + dayOffset + (nth - 1) * 7;
};

// Helper function to calculate the last weekday of a month
const getLastWeekdayOfMonth = (weekday, month, year) => {
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const lastWeekday = lastDayOfMonth.getDay();
  const dayOffset = (lastWeekday - weekday + 7) % 7;
  return lastDayOfMonth.getDate() - dayOffset;
};

const isObservedHoliday = (date, month, year, holidayDate, holidayMonth) => {
  const holiday = new Date(year, holidayMonth, holidayDate);
  const holidayDay = holiday.getDay();

  if (holidayDay === 6) {
    // Holiday is on a Saturday, observe it on Friday
    return date === holidayDate - 1 && month === holidayMonth;
  } if (holidayDay === 0) {
    // Holiday is on a Sunday, observe it on Monday
    return date === holidayDate + 1 && month === holidayMonth;
  }
  // Regular holiday
  return date === holidayDate && month === holidayMonth;
};

// IMPORTANT: interpret the *instant* in the factory calendar before applying rules.
const isHoliday = (inputDate) => {
  if (!(inputDate instanceof Date)) throw new Error('Invalid date input');
  const year = +fmtInTZ(inputDate, FACTORY_TZ, 'yyyy');
  const month = +fmtInTZ(inputDate, FACTORY_TZ, 'MM') - 1; // 0-based
  const date = +fmtInTZ(inputDate, FACTORY_TZ, 'dd');
  const isoDow = +fmtInTZ(inputDate, FACTORY_TZ, 'i'); // 1..7 (Mon..Sun)
  const dow = isoDow % 7; // 0=Sun..6=Sat

  const thanksgiving = getNthWeekdayOfMonth(4, 4, 10, year); // 4th Thursday in November
  // check all the weekends and holidays
  return (dow === 0 || dow === 6) // weekends
    // New Year's Day - fixed
    || isObservedHoliday(date, month, year, 1, 0)
    // Martin Luther King Jr. Day - 3rd Monday in January
    || (date === getNthWeekdayOfMonth(3, 1, 0, year) && month === 0)
    // Patriot's Day - 3rd Monday in April
    || (date === getNthWeekdayOfMonth(3, 1, 3, year) && month === 3)
    // Memorial Day - Last Monday in May
    || (date === getLastWeekdayOfMonth(1, 4, year) && month === 4)
    // Juneteenth - fixed
    || isObservedHoliday(date, month, year, 19, 5)
    // Independence Day - fixed
    || isObservedHoliday(date, month, year, 4, 6)
    // Labor Day - 1st Monday in September
    || (date === getNthWeekdayOfMonth(1, 1, 8, year) && month === 8)
    // Indigenous Peoples' Day - 2nd Monday in October
    || (date === getNthWeekdayOfMonth(2, 1, 9, year) && month === 9)
    // Veterans Day - fixed
    || isObservedHoliday(date, month, year, 11, 10)
    || (date === 25 && month === 10 && year === 2024) // Thanksgiving Monday only for 2024
    || (date === 26 && month === 10 && year === 2024) // Thanksgiving Tuesday only for 2024
    || (date === 27 && month === 10 && year === 2024) // Thanksgiving Wednesday only for 2024
    // Thanksgiving - getNthWeekdayOfMonth(4, 4, 10, year)
    || (date === thanksgiving && month === 10)
    // day after Thanksgiving
    || (date === thanksgiving + 1 && month === 10)
    // check if Christmas Eve is on a weekend
    || isObservedHoliday(date, month, year, 24, 11)
    // Christmas
    || (date > 23 && month === 11);
};

/* ---------------- Parsing & calculations (factory TZ) ---------------- */

// Add N business days to "now" (factory calendar rules, 5pm cutoff)
// Returns a UTC instant representing local midnight of the resulting factory day.
const getShipDateByDays = (days, from = null) => {
  let daysToAdd = parseInt(days, 10);

  let now = systemClock();
  if (from) {
    if (from instanceof Date) {
      if (Number.isNaN(+from)) {
        throw new Error('Invalid date input');
      }
      now = from;
    } else {
      throw new Error('Invalid date input');
    }
  }

  // Determine if we're after today's cutoff in factory time
  const afterCutoff = now.getTime() >= cutoffUTC(now).getTime();

  // Start cursor at the factory local midnight for stability
  let cursorUTC = startOfFactoryDayUTC(now);

  // if the order is placed on a holiday, keep adding days until it's not a holiday
  while (isHoliday(cursorUTC)) {
    cursorUTC = addFactoryDaysUTC(cursorUTC, 1);
  }

  // if ordered after 5pm on a weekday, add one more day
  if (afterCutoff) {
    daysToAdd += 1;
  }

  let daysAdded = 0;
  while (daysAdded < daysToAdd) {
    const next = addFactoryDaysUTC(cursorUTC, 1);
    cursorUTC = next;
    if (!isHoliday(cursorUTC)) {
      daysAdded += 1;
    }
  }
  return cursorUTC; // UTC instant at local midnight of the ship day (factory tz)
};

// Parse "YYYY-MM-DD" as a *factory local* date, else interpret an instant and return that day's local midnight (UTC)
const parseTargetDate = (futureDate) => {
  if (typeof futureDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(futureDate)) {
    return factoryLocalToUTC({ ymd: futureDate });
  }
  const dt = new Date(futureDate);
  if (Number.isNaN(dt.valueOf())) throw new Error('Invalid date');
  return startOfFactoryDayUTC(dt);
};

const calculateBusinessDays = (futureDate) => {
  const now = systemClock();
  const todayUTC = startOfFactoryDayUTC(now);
  const endUTC = parseTargetDate(futureDate);

  // Ensure the date is in the future
  if (endUTC < todayUTC) {
    return -1;
  }

  let businessDays = 0;
  let cursor = todayUTC;

  // Iterate through days until reaching the future date
  while (cursor < endUTC) {
    if (!isHoliday(cursor)) {
      businessDays += 1;
    }
    cursor = addFactoryDaysUTC(cursor, 1);
  }

  return businessDays;
};

const formatAddress = (address = {}) => {
  const {
    name, company, street1, street2, city, state, zip, country,
  } = address;

  if (!street1) return 'No address provided';

  // Compose the address parts
  const shipName = company ? `Attn: ${name}` : name;
  const line1 = street1;
  const line2 = street2 ? `${street2}` : '';
  const line3 = `${city}, ${state} ${zip}`;
  const line4 = country ? `${country}` : '';

  // Return the formatted address
  return [company, shipName, line1, line2, line3, line4].filter(Boolean).join('\n'); // Filter out empty lines
};

const isInternationalShipment = (toAddress = {}) => {
  const { country, state } = toAddress;
  if (!country) return false;

  // If country is not US, definitely international
  if (country.toUpperCase() !== 'US') return true;

  if (!state) return false;

  // US territories and military states that still need customs
  const US_TERRITORIES_AND_MILITARY = ['PR', 'GU', 'VI', 'AS', 'MP', 'AA', 'AE', 'AP'];

  // If country is US but state is in special list, still needs customs
  if (country.toUpperCase() === 'US' && US_TERRITORIES_AND_MILITARY.includes(state.toUpperCase())) {
    return true;
  }

  return false;
};

export {
  getShipDateByDays,
  formatWeekDate,
  formatDate,
  calculateBusinessDays,
  formatAddress,
  factoryLocalToUTC,
  isHoliday,
  isInternationalShipment,
};
